/* eslint-disable react-hooks/rules-of-hooks */
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { Button, Form, Popconfirm } from 'antd';
import Tooltip from '@/components/Tooltip';
import { RouteLoader } from '@dofleini/security';
import { useTranslation } from 'react-i18next';
import FloatContainer from '@/components/FloatContainer';
import useCrud from '@/hooks/useCrud';
import { useHistory } from 'react-router-dom';
import FormSkeleton from '@/components/FormGenerator/FormSkeleton';
import { useFormTitle } from '@/hooks/useFormTitle';
import OpenDetail from '@/components/OpenDialog';
import { DeleteOutlined, FormOutlined } from '@ant-design/icons';
import { EntityDetailContent } from '@/components/EntityDetailContent';
import FormBuilder from 'antd-form-builder';
import { DetailsContextProvider, useDetailsContext } from '@/contexts/DetailsContext';
import { createReferenceWarning } from '@/utils/createReferenceWarning';
import isEmpty from 'lodash/isEmpty';
import useDeepCompareEffect from 'use-deep-compare-effect';

export const headerActions = ({
  data,
  t,
  viewMode,
  onSubmit,
  toggleView,
  handleClose,
  onDelete,
  isLoading,
  headerProps = {},
  form
}) => {
  const { extras, canRemove, canEdit, extrasEdit, hideSaveEdit, hideCancelEdit } = headerProps;
  const ExtraOptions = extras;
  const ExtraOptionsEdit = extrasEdit;
  if (viewMode)
    return [
      ExtraOptions &&
      <ExtraOptions key={'detail-extra-actions'} data={data} isLoading={isLoading} handleClose={handleClose} t={t}/>,
      canEdit && <Tooltip parentContainer title={t('edit')} key={'edit'}>
        <Button type="text" icon={<FormOutlined/>} onClick={toggleView} disabled={isLoading}/>
      </Tooltip>,
      canRemove && <Popconfirm
        key={'remove'}
        placement="bottomRight"
        title={t('deleteConfirmation')}
        onConfirm={onDelete}
        okText={t('delete')}
        cancelText={t('cancel')}
      >
        <Tooltip parentContainer title={t('delete')}>
          <Button type="text" icon={<DeleteOutlined/>} loading={isLoading}/>
        </Tooltip>
      </Popconfirm>
    ];
  return (
    [
      ExtraOptionsEdit && <ExtraOptionsEdit
        data={data}
        onSubmit={onSubmit}
        isLoading={isLoading}
        onCancel={handleClose}
        form={form}/>,

      !hideSaveEdit && <Button
        key="submit"
        type="primary"
        onClick={onSubmit}
        loading={isLoading}
        disabled={isLoading}
      >
        {t('save')}
      </Button>,
      !hideCancelEdit && <Button key="back" onClick={handleClose} disabled={isLoading}>
        {t('cancel')}
      </Button>,
    ]
  );
};

export const createEntityDetail = (
  {
    route, service, name, module, translation, translationPrefix, useFields, useGetOne,
    options = {
      create: true,
      edit: true,
      details: true
    },
    willForceUpdate,
    getOneKey = 'getOne',
    mapPayload = v => v,
    mapResponse = v => v,
    messageReferenceWarningDialog,
    columns,
    entityName,
    customHeaderProps,
    ...props
  }) => {
  const dataMock = {};
  const {
    useErrorMiddleware,
    ReferenceWarningDialog,
  } = createReferenceWarning({
    translation,
    service,
    module,
    message: messageReferenceWarningDialog,
  });

  // eslint-disable-next-line react/display-name
  const FormComponent = memo(({
    customTitleKey,
    headerProps,
    onMountData,
    customFeedBack,
    noRedirect,
    onCloseCallback
  }) => {
    const { t } = useTranslation(translation);
    const { push } = useHistory();
    const { isOpen, closeDialog, payload } = useDetailsContext(name);
    const {
      create,
      update,
      remove,
      isLoading: isCrudLoading
    } = useCrud(service, module, getOneKey, customFeedBack || payload?.customFeedBack);
    const errorHandler = useErrorMiddleware();
    const { isLoading, data: res } = useGetOne(payload);
    const [form] = Form.useForm();
    const { title, viewMode, toggleView, backToView } = useFormTitle(payload?.forceCreate, payload?.forceEdit);
    const forceUpdate = FormBuilder.useForceUpdate();

    const data = useMemo(() => mapResponse(res?.data), [res]);

    const defaultValues = Object.assign(payload || {}, data || {});

    const initValues = useMemo(() => (!data?._id ? defaultValues : data) || dataMock, [data, defaultValues]);
    const id = initValues?._id;
    const fields = useFields(viewMode, form, id);

    useEffect(() => {
      (data && onMountData) && onMountData([data]);
    }, [data, onMountData]);

    useEffect(() => {
      setTimeout(() => forceUpdate(), 100);
    }, [forceUpdate, id]);

    const close = useCallback(() => {
      closeDialog();
      setTimeout(() => {
        if (!noRedirect)
          route && push(route);
        form.resetFields();
      }, 500);
      onCloseCallback && onCloseCallback(push, route);
    }, [closeDialog, form, noRedirect, onCloseCallback, push]);

    const onClose = useCallback(() => {
      if (backToView) {
        return toggleView();
      }
      close();
    }, [backToView, close, toggleView]);

    const handleFinish = useCallback(async (values) => {
      if (id) {
        const { data } = await update({ _id: id, ...values });
        form.setFieldsValue(mapResponse(data));
      } else
        await create(values);
      onClose();
    }, [id, create, onClose, update, form]);

    const onFinish = useCallback((values) => {
      const mappedValues = mapPayload(values);

      handleFinish(mappedValues).catch(
        errorHandler({
          onForce: () => handleFinish({ force: true, ...mappedValues }),
          onRecovery: () => close()
        })
      );

    }, [close, errorHandler, handleFinish]);

    const handleDelete = useCallback(async () => {
      if (initValues) {
        await remove(initValues?._id);
        onClose();
      }
    }, [onClose, initValues, remove]);

    useDeepCompareEffect(() => {
      if (!isEmpty(initValues)) {
        form.setFieldsValue(initValues);
      }
    }, [initValues]);

    return (
      <FloatContainer
        {...props}
        visible={isOpen}
        title={t(`${translationPrefix}.${customTitleKey || title}`)}
        onClose={onClose}
        footer={headerActions({
          headerProps,
          isLoading: isLoading || isCrudLoading,
          handleClose: onClose,
          data: initValues,
          onDelete: handleDelete,
          onSubmit: () => form.submit(),
          entityName: entityName,
          t,
          toggleView,
          viewMode,
          form
        })}>
        <Form
          form={form} onFinish={onFinish} layout={!viewMode ? 'vertical' : 'horizontal'}
          onValuesChange={willForceUpdate ? forceUpdate : false}>
          {isLoading ?
            <FormSkeleton/> :
            <EntityDetailContent viewMode={viewMode} data={initValues} form={form} fields={fields} columns={columns}/>
          }
        </Form>
      </FloatContainer>
    );
  });

  const pathRoute = [];
  if (options?.create) {
    route && pathRoute.push(`${route}/create`);
  }
  if (options?.details) {
    route && pathRoute.push(`${route}/:id`);
  }
  if (options?.edit) {
    route && pathRoute.push(`${route}/:id/edit`);
  }

  const routes = {
    NestedView: {
      path: pathRoute,
      exact: true,
      data: {
        dialog: name
      },
      component: OpenDetail
    }
  };

  // eslint-disable-next-line react/display-name
  const Detail = memo((props) => {
    return <DetailsContextProvider id={module}>
      {/* eslint-disable-next-line react/prop-types */}
      <FormComponent {...props} headerProps={customHeaderProps ? customHeaderProps : props?.headerProps}/>
      <RouteLoader routes={routes} notfoundRedirect={props?.noRedirect ? null : route}/>
      <ReferenceWarningDialog/>
    </DetailsContextProvider>;
  });

  Detail.propTypes = {
    noRedirect: PropTypes.bool,
  };

  FormComponent.propTypes = {
    dataMock: PropTypes.object,
    noRedirect: PropTypes.bool,
    customTitleKey: PropTypes.string,
    headerProps: PropTypes.object,
    customFeedBack: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    onMountData: PropTypes.func,
    onCloseCallback: PropTypes.func,
  };

  FormComponent.defaultProps = {
    headerProps: { canRemove: true, canEdit: true }
  };
  return { FormComponent, Detail };
};

createEntityDetail.propTypes = {
  module: PropTypes.any,
  entityName: PropTypes.any,
  name: PropTypes.any,
  options: PropTypes.object,
  route: PropTypes.any,
  service: PropTypes.any,
  translation: PropTypes.any,
  translationPrefix: PropTypes.any,
  useFields: PropTypes.any,
  useGetOne: PropTypes.any,
  willForceUpdate: PropTypes.bool,
  mapPayload: PropTypes.func,
};
